import type {
  ApiErrorDto,
  BudgetDto,
  BudgetPatchDto,
  BudgetsResponseDto,
  CategoryCreateDto,
  CategoryDto,
  CategoryPatchDto,
  OverviewDto,
  PaymentCreateDto,
  PaymentDto,
  PaymentPaidPatchDto,
  PaymentPaidResultDto,
  PaymentPatchDto,
  PaymentsResponseDto,
  RateDto,
  SettingsDto,
  SettingsPatchDto,
  SubscriptionCreateDto,
  SubscriptionDto,
  SubscriptionPatchDto,
  SubscriptionsResponseDto,
  TransactionCreateDto,
  TransactionDto,
  TransactionPatchDto,
} from '@trestly/shared';
import { getInitDataRaw } from '../telegram';
import { t } from '@/i18n';

/** Пусто = тот же origin (прод за Caddy, дев через vite-proxy). */
const BASE_URL = import.meta.env.VITE_API_URL ?? '';

/** Ошибка API с разобранным телом ApiErrorDto. */
export class ApiError extends Error {
  readonly statusCode: number;
  readonly error: string;

  constructor(dto: ApiErrorDto) {
    super(dto.message);
    this.name = 'ApiError';
    this.statusCode = dto.statusCode;
    this.error = dto.error;
  }
}

type Query = Record<string, string | number | boolean | undefined | null>;

function buildUrl(path: string, query?: Query): string {
  const url = `${BASE_URL}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

interface RequestOptions {
  // PUT нужен для upsert'а бюджета (PUT /api/budgets).
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Query;
  body?: unknown;
}

/**
 * Тело upsert'а лимита. Отдельного DTO в @trestly/shared нет: эндпоинт
 * принимает period ЛИБО periodId, чего BudgetPatchDto не описывает.
 */
export interface BudgetUpsertBody {
  period?: string;
  periodId?: number;
  categoryId: number;
  limitMinor: number;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', query, body } = options;

  const headers: Record<string, string> = { Accept: 'application/json' };
  // Подпись проверяется сервером на КАЖДОМ запросе, поэтому заголовок
  // проставляем всегда, а не один раз при открытии приложения.
  const initDataRaw = getInitDataRaw();
  if (initDataRaw) headers.Authorization = `tma ${initDataRaw}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (cause) {
    const timeout = cause instanceof DOMException && cause.name === 'TimeoutError';
    throw new ApiError({
      error: timeout ? 'TimeoutError' : 'NetworkError',
      message: timeout ? t('lib.error.timeout') : t('lib.error.network'),
      statusCode: 0,
    });
  }

  if (!response.ok) {
    throw new ApiError(await parseError(response));
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

async function parseError(response: Response): Promise<ApiErrorDto> {
  try {
    const parsed: unknown = JSON.parse(await response.text());
    if (isApiErrorDto(parsed)) return parsed;
  } catch {
    /* тело не JSON — соберём ошибку из статуса */
  }
  return {
    error: response.statusText || 'HttpError',
    message: t('lib.error.status', { n: response.status }),
    statusCode: response.status,
  };
}

function isApiErrorDto(value: unknown): value is ApiErrorDto {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.error === 'string' && typeof v.message === 'string' && typeof v.statusCode === 'number';
}

/** Типизированные методы под раздел «API (черновик)» из PLAN.md. */
export const api = {
  health: (): Promise<{ status: string }> => request('/health'),

  /** Всё для главной одним запросом — минимизируем round-trip'ы в мобильной сети. */
  overview: (period?: string): Promise<OverviewDto> => request('/api/overview', { query: { period } }),

  settings: {
    get: (): Promise<SettingsDto> => request('/api/settings'),
    patch: (body: SettingsPatchDto): Promise<SettingsDto> =>
      request('/api/settings', { method: 'PATCH', body }),
  },

  subscriptions: {
    list: (): Promise<SubscriptionsResponseDto> => request('/api/subscriptions'),
    create: (body: SubscriptionCreateDto): Promise<SubscriptionDto> =>
      request('/api/subscriptions', { method: 'POST', body }),
    patch: (id: number, body: SubscriptionPatchDto): Promise<SubscriptionDto> =>
      request(`/api/subscriptions/${id}`, { method: 'PATCH', body }),
    /** Удаление мягкое (archive) — история месяцев не должна разъезжаться. */
    remove: (id: number): Promise<void> => request(`/api/subscriptions/${id}`, { method: 'DELETE' }),
  },

  payments: {
    list: (period?: string): Promise<PaymentsResponseDto> =>
      request('/api/payments', { query: { period } }),
    create: (body: PaymentCreateDto): Promise<PaymentDto> =>
      request('/api/payments', { method: 'POST', body }),
    patch: (id: number, body: PaymentPatchDto): Promise<PaymentDto> =>
      request(`/api/payments/${id}`, { method: 'PATCH', body }),
    setPaid: (id: number, period: string, body: PaymentPaidPatchDto): Promise<PaymentPaidResultDto> =>
      request(`/api/payments/${id}/paid`, { method: 'POST', query: { period }, body }),
  },

  categories: {
    // includeArchived: история месяцев может ссылаться на архивные категории,
    // а им нужны имена.
    list: (includeArchived?: boolean): Promise<CategoryDto[]> =>
      request('/api/categories', {
        query: { includeArchived: includeArchived ? '1' : undefined },
      }),
    create: (body: CategoryCreateDto): Promise<CategoryDto> =>
      request('/api/categories', { method: 'POST', body }),
    patch: (id: number, body: CategoryPatchDto): Promise<CategoryDto> =>
      request(`/api/categories/${id}`, { method: 'PATCH', body }),
  },

  budgets: {
    list: (period?: string): Promise<BudgetsResponseDto> =>
      request('/api/budgets', { query: { period } }),
    // PATCH возвращает ОДНУ строку бюджета, а не весь список периода.
    patch: (id: number, body: BudgetPatchDto): Promise<BudgetDto> =>
      request(`/api/budgets/${id}`, { method: 'PATCH', body }),
    /**
     * Upsert по (period, categoryId): строки budgets может ещё не быть —
     * у новой категории в списке приходит id: 0, и PATCH /api/budgets/0 даст 404.
     * Возвращает ОДНУ строку BudgetDto, а не весь BudgetsResponseDto.
     */
    upsert: (body: BudgetUpsertBody): Promise<BudgetDto> =>
      request('/api/budgets', { method: 'PUT', body }),
  },

  transactions: {
    list: (params: { period?: string; category?: number }): Promise<TransactionDto[]> =>
      request('/api/transactions', { query: { period: params.period, category: params.category } }),
    create: (body: TransactionCreateDto): Promise<TransactionDto> =>
      request('/api/transactions', { method: 'POST', body }),
    patch: (id: number, body: TransactionPatchDto): Promise<TransactionDto> =>
      request(`/api/transactions/${id}`, { method: 'PATCH', body }),
    remove: (id: number): Promise<void> => request(`/api/transactions/${id}`, { method: 'DELETE' }),
  },

  rates: {
    get: (on?: string): Promise<RateDto> => request('/api/rates', { query: { on } }),
  },
};
